/**
 * Binding an inline citation anchor in the answer to the evidence event it
 * cites.
 *
 * The answer and the audit trail are produced by different parts of the
 * backend and meet only here. An anchor carries a `doppler://` href; an
 * evidence event carries typed `source_refs`. This module decides when the two
 * describe the same source, so a click on a phrase can put the right card in
 * the trail.
 *
 * Two anchor forms, for two reasons:
 *
 * - **`doppler://cite/<event_id>`** — the direct form. Names an evidence event
 *   outright and is what a tool emits when it knows the event it just wrote.
 *   No matching required and no ambiguity possible.
 * - **`doppler://pdf|excel|doc/...`** — the incidental form. These already
 *   exist in answers as *navigation* links (the marker post-processor emits
 *   them), and they happen to point at the same sources the evidence events
 *   describe. Matching them structurally means those answers gain the chain
 *   without the backend re-emitting anything.
 *
 * Matching is structural, never string equality: the same Excel cell is
 * spelled several ways across producers (percent-encoding, an `&file=` display
 * name, sheet-only vs cell-level), and comparing the parsed locator fields is
 * the only comparison that survives that.
 */

import type { EvidenceEvent, SourceRef } from "../types/evidence";
import { FileType } from "../types/fileType";
import { parseDopplerUri, type ParsedDopplerUri } from "./dopplerUri";

/** What an anchor resolved to, and the key the chain dedupes on. */
export interface ResolvedCitation {
	/** Stable identity for this citation within a message. */
	key: string;
	/** The evidence event, when the anchor could be bound to one. */
	event: EvidenceEvent | null;
	/** The parsed href, kept so an unbound anchor can still render something. */
	target: ParsedDopplerUri;
}

const sameNumber = (a: unknown, b: unknown): boolean =>
	a != null && b != null && Number(a) === Number(b);

/** Case- and punctuation-insensitive compare for sheet/cell spellings. */
const looseEqual = (a: string | null | undefined, b: string | null | undefined) =>
	(a ?? "").replace(/[$\s]/g, "").toLowerCase() ===
	(b ?? "").replace(/[$\s]/g, "").toLowerCase();

/**
 * Does this source ref describe what the parsed anchor points at?
 *
 * Deliberately lenient on the locator WITHIN a file and strict on the file
 * itself: an anchor naming a whole workbook should bind to a cell-level event
 * in that workbook (that is the citation the reader wants), but an anchor
 * naming file 12 must never bind to an event about file 13.
 */
function refMatchesTarget(ref: SourceRef, target: ParsedDopplerUri): boolean {
	switch (target.kind) {
		case FileType.PDF:
			return (
				ref.kind === "pdf" &&
				sameNumber(ref.file_id, target.target) &&
				// A page-less anchor matches any page of the document.
				(target.page == null || sameNumber(ref.page, target.page))
			);
		case FileType.EXCEL:
			if (ref.kind !== "excel") return false;
			if (target.subkind === "artifact") return ref.artifact_id === target.target;
			return (
				sameNumber(ref.file_id, target.target) &&
				(target.sheet == null || looseEqual(ref.sheet, target.sheet)) &&
				(target.cell == null || looseEqual(ref.cell, target.cell))
			);
		case FileType.DOC:
			return (
				ref.kind === "comms" &&
				sameNumber(ref.file_id, target.target) &&
				(target.message == null ||
					sameNumber(ref.message_ordinal, target.message))
			);
		default:
			return false;
	}
}

/**
 * Resolve an anchor href against the active message's evidence.
 *
 * `null` when the href is not a citation anchor at all — an ordinary link in
 * the answer must keep behaving like one.
 */
export function resolveCitation(
	href: string | undefined,
	events: EvidenceEvent[],
): ResolvedCitation | null {
	if (!href) return null;
	const target = parseDopplerUri(href);
	if (!target) return null;

	if (target.kind === FileType.CITE) {
		const event = events.find((e) => e.event_id === target.target) ?? null;
		// The key is the EVENT id whenever there is one, so the same citation
		// reached two ways — a `cite` anchor and a `doppler://pdf` link for the
		// same source — dedupes to one card rather than stacking two.
		//
		// An unresolvable anchor keeps its own id and is still returned: a
		// `cite` href is unambiguously a citation, so it should render as one
		// and say it could not be loaded rather than silently becoming a dead
		// link.
		return { key: event?.event_id ?? `cite:${target.target}`, event, target };
	}

	const event =
		events.find((e) => e.source_refs.some((ref) => refMatchesTarget(ref, target))) ??
		null;
	if (!event) {
		// An unmatched file href is just a navigation link — the case that was
		// there before any of this existed. Leave it alone.
		return null;
	}
	return { key: event.event_id, event, target };
}

/**
 * Is this href a citation anchor for the given message?
 *
 * Convenience for the renderer, which asks the question before it decides
 * which kind of `<a>` to build.
 */
export const isCitationAnchor = (
	href: string | undefined,
	events: EvidenceEvent[],
): boolean => resolveCitation(href, events) !== null;

/** Where a citation's document lives, when the anchor can open it directly. */
export interface PdfCitationTarget {
	/** A `doppler://pdf` href carrying the page and the quote to highlight. */
	href: string;
	/** Display name for the viewer header, when the evidence knows it. */
	filename?: string;
}

/**
 * The document a PDF citation should open on the FIRST click, or `null` when
 * the citation is not a single unambiguous PDF page.
 *
 * A cited phrase used to cost two clicks: one to summon the card, another on
 * the card to reach the document. For a PDF the card adds nothing the viewer
 * does not already show, so the anchor opens the page itself and the card is
 * still appended behind it — the audit trail is unchanged, the round trip is
 * gone. Other kinds keep the two-step: an Excel cell or a Slack message is
 * genuinely better read as a card first.
 *
 * `null` for anything ambiguous. An event citing two different pages is a "see
 * pages 4 and 9" citation, and picking one of them would be a guess dressed up
 * as navigation; the card lists both and lets the reader choose.
 */
export function pdfCitationTarget(
	citation: ResolvedCitation,
): PdfCitationTarget | null {
	const { target, event } = citation;
	const refs = event?.source_refs ?? [];
	const pdfRefs = refs.filter((ref): ref is Extract<SourceRef, { kind: "pdf" }> =>
		ref.kind === "pdf",
	);

	if (target.kind === FileType.CITE) {
		// The anchor names an event, not a file, so everything comes from the
		// evidence — and only when the evidence points at exactly one page.
		if (!pdfRefs.length || pdfRefs.length !== refs.length) return null;
		const [first] = pdfRefs;
		const singlePage = pdfRefs.every(
			(ref) => sameNumber(ref.file_id, first.file_id) && ref.page === first.page,
		);
		if (!singlePage) return null;
		return {
			href: buildPdfHref(String(first.file_id), first.page, first.excerpt),
			filename: first.filename ?? undefined,
		};
	}

	if (target.kind !== FileType.PDF) return null;
	// The anchor already names the file and page; the bound event only supplies
	// what the href left out — the quote to highlight and the display name.
	const ref = pdfRefs.find((r) => sameNumber(r.file_id, target.target)) ?? null;
	return {
		href: buildPdfHref(
			target.target,
			target.page ?? ref?.page,
			target.highlight ?? ref?.excerpt,
		),
		filename: target.filename ?? ref?.filename ?? undefined,
	};
}

const buildPdfHref = (
	fileId: string,
	page?: number | null,
	highlight?: string | null,
): string => {
	const params = new URLSearchParams();
	if (page != null) params.set("page", String(page));
	if (highlight) params.set("hl", highlight);
	const query = params.toString();
	return `doppler://pdf/${fileId}${query ? `?${query}` : ""}`;
};
